// --- Action ids (sf_support.actions.id) ---
// REPLACE_NODE is materialized at WorkTasksPage to bucket replacement tasks.
// Mirror in lockstep with the actions table — server expects these exact ids.
export const ACTION_IDS = {
  REPLACE_NODE: 2,
} as const;

// --- Node / detail panel rows ---
// These mirror the shapes returned by GET /api/nodes/:macId/{detail,
// firmware-history, history, ...}. Loose-typed today because the source
// queries union several upstream views; tighten one field at a time as
// columns stabilize.

// Mirrors the SELECT in server/src/routes/nodes.ts (the /:macId handler).
// `gateway_id`/`location_id`/`firmware_version` were never in the response;
// the regression sweep flagged them as silently undefined. Realigned with
// actual columns so consumers don't read fields that will always be
// undefined.
export interface NodeDetail {
  node_mac_id: string;
  node_type: string | null;
  node_sub_type: string | null;
  current_firmware_version_number: string | number | null;
  firmware_version_name: string | null;
  last_seen: string | null;
  // Index signature preserved so untyped consumers (e.g. the loose loops in
  // IssueDetailPane) don't immediately break — but field names that aren't
  // in the SELECT above will return undefined.
  [key: string]: unknown;
}

export interface BatteryStatus {
  battery_percentage: number | null;
  battery_voltage: number | null;
  ts: string | null;
  [key: string]: unknown;
}

// (Gateway is declared canonically below at the original definition site —
// PR6's loose-typed alias has been removed to avoid duplicate interface
// declarations.)

export interface NodeHistoryEvent {
  ts: string;
  event_type: string;
  details: string | null;
  [key: string]: unknown;
}

export interface FirmwareEvent {
  from_firmware: string | null;
  to_firmware: string | null;
  result: string;
  created_time: string | null;
  start_time: string | null;
  end_time: string | null;
  [key: string]: unknown;
}

// --- /api/nodes/:macId response shape ---
// Mirrors server/src/routes/nodes.ts:155-161. Keep in sync if either side
// changes — TypeScript will not catch upstream JSON drift on its own.

export interface NodeBatteryStatus {
  day: string | null;
  avg_battery_lvl: number | null;
  estimated_battery_left_perc: number | null;
  months_of_life_left: number | null;
  current_age: number | null;
  battery_type: string | null;
  suggested_action: string | null;
}

export interface NodeGatewaySummary {
  balena_id: string;
  balena_device_name: string | null;
  network_status: string | null;
  last_heartbeat_time: string | null;
  is_online: boolean;
}

export interface NodeMappingHistoryRow {
  mapped_time: string;
  decommissioned_time: string | null;
  comment: string | null;
  position_name: string | null;
  location_name: string | null;
  [key: string]: unknown;
}

export interface NodePastTicket {
  ticket_id: number;
  ticket_created: string;
  ticket_class_id: number | null;
  ticket_status_id: number | null;
  action_id: number | null;
  root_cause_id: number | null;
  ticket_class: string | null;
  status_name: string | null;
  action_name: string | null;
  root_cause_name: string | null;
}

export interface NodeRecovery {
  recovered_date: string | null;
  time_to_recovery_days: number | string | null;
  is_recovered: boolean;
  is_gap_affected: boolean;
  offline_since_day: string | null;
}

export interface NodeDetailResponse {
  node: NodeDetail | null;
  mappingHistory: NodeMappingHistoryRow[];
  batteryStatus: NodeBatteryStatus | null;
  // #5: battery snapshot as of the offline moment (only present when fetched
  // with an `asOf` date). Canonical for diagnosis; `batteryStatus` is "now".
  batteryAtOffline?: NodeBatteryStatus | null;
  gateway: NodeGatewaySummary | null;
  pastTickets: NodePastTicket[];
  // Recovery state from sensorflow_analytics.m_node_recovery_lookup. Tells the operator
  // whether the node has come back online since its most recent offline event
  // and how long that took. Null when the model has no row for this mac yet.
  recovery: NodeRecovery | null;
}

// --- Ticket classes for fleet monitoring ---
export const TICKET_CLASS_IDS = {
  BATTERY_DRAINED: 5,
  OFFLINE_UNKNOWN: 10,
  HARDWARE_FAULT: 54,
  HARDWARE_FAULT_EOL: 55,
  TAMPERING: 56,
  FIRMWARE_ISSUE: 57,
  OUT_OF_RANGE: 58,
  ON_SITE_INVESTIGATION: 59,
  FALSE_ALERT: 60,
} as const;

export const FLEET_CLASS_IDS = [
  TICKET_CLASS_IDS.BATTERY_DRAINED,
  TICKET_CLASS_IDS.OFFLINE_UNKNOWN,
  TICKET_CLASS_IDS.HARDWARE_FAULT,
  TICKET_CLASS_IDS.HARDWARE_FAULT_EOL,
  TICKET_CLASS_IDS.TAMPERING,
  TICKET_CLASS_IDS.FIRMWARE_ISSUE,
  TICKET_CLASS_IDS.OUT_OF_RANGE,
  TICKET_CLASS_IDS.ON_SITE_INVESTIGATION,
  TICKET_CLASS_IDS.FALSE_ALERT,
] as const;

// --- Ticket statuses ---
export const TICKET_STATUS = {
  PENDING: 1,
  ARCHIVED: 2,
  BLOCKED: 3,
  DONE: 4,
  AUTO_RESOLVED: 6,
  IN_PROGRESS: 7,
  IN_JRF: 9,
  FALSE_ALERT: 10,
  WAITING_FOR_CLIENT: 11,
  IN_REVIEW: 13,
  WILL_NOT_FIX: 14,
  WAITING_FOR_ACTION: 18,
  CANCELED: 20,
} as const;

export const STATUS_NAMES: Record<number, string> = {
  [TICKET_STATUS.PENDING]: 'Pending',
  [TICKET_STATUS.ARCHIVED]: 'Archived',
  [TICKET_STATUS.BLOCKED]: 'Blocked',
  [TICKET_STATUS.DONE]: 'Done',
  [TICKET_STATUS.AUTO_RESOLVED]: 'Auto-Resolved',
  [TICKET_STATUS.IN_PROGRESS]: 'In Progress',
  [TICKET_STATUS.IN_JRF]: 'In JRF',
  [TICKET_STATUS.FALSE_ALERT]: 'False Alert',
  [TICKET_STATUS.WAITING_FOR_CLIENT]: 'Waiting for Client',
  [TICKET_STATUS.IN_REVIEW]: 'In Review',
  [TICKET_STATUS.WILL_NOT_FIX]: 'Will Not Fix',
  [TICKET_STATUS.WAITING_FOR_ACTION]: 'Waiting for Action',
  [TICKET_STATUS.CANCELED]: 'Canceled',
};

// Statuses considered "open" for filtering
export const OPEN_STATUS_IDS = [
  TICKET_STATUS.PENDING,
  TICKET_STATUS.IN_PROGRESS,
  TICKET_STATUS.BLOCKED,
  TICKET_STATUS.WAITING_FOR_CLIENT,
  TICKET_STATUS.IN_REVIEW,
  TICKET_STATUS.WAITING_FOR_ACTION,
  TICKET_STATUS.IN_JRF,
];

// Statuses considered "closed" (excluded from open ticket queries)
export const CLOSED_STATUS_IDS = [
  TICKET_STATUS.ARCHIVED,
  TICKET_STATUS.DONE,
  TICKET_STATUS.AUTO_RESOLVED,
  TICKET_STATUS.CANCELED,
];

// --- Triage action definitions ---
export interface TriageActionDef {
  key: string;
  label: string;
  shortLabel: string;
  ticketClassId: number | null; // reclassify ticket to this class (null = keep current)
  rootCauseId: number | null;   // null = keep current
  actionId: number | null;      // null = keep current
  statusId: number;
  color: string;
  shortcutKey: string;
}

export const TRIAGE_ACTIONS: TriageActionDef[] = [
  {
    key: 'battery',
    label: 'Battery Drained',
    shortLabel: 'Battery',
    ticketClassId: TICKET_CLASS_IDS.BATTERY_DRAINED,
    // rootCauseId 11 is the closest existing root cause for natural cell
    // depletion ("battery is too old"). Distinct from the EOL action below,
    // which uses the same root cause but maps to HARDWARE_FAULT_EOL —
    // EOL is "whole node past lifecycle"; BATTERY_DRAINED is "cells need
    // changing on an otherwise-healthy node." actionId left null because
    // there is no canonical "change batteries" action row in
    // sf_support.actions at the moment; operator can attach one via the
    // post-triage task prompt if needed.
    rootCauseId: 11,
    actionId: null,
    statusId: TICKET_STATUS.WAITING_FOR_ACTION,
    color: '#fadb14', // amber — distinct from Hardware red + EOL purple (U7.1)
    shortcutKey: '1',
  },
  {
    key: 'hardware',
    label: 'Hardware Issue',
    shortLabel: 'Hardware',
    ticketClassId: TICKET_CLASS_IDS.HARDWARE_FAULT,
    rootCauseId: 3,  // hardware issue
    actionId: 2,     // replace node
    statusId: TICKET_STATUS.WAITING_FOR_ACTION,
    color: '#ff4d4f',
    shortcutKey: '2',
  },
  {
    key: 'hardware_eol',
    label: 'Hardware EOL',
    shortLabel: 'HW EOL',
    ticketClassId: TICKET_CLASS_IDS.HARDWARE_FAULT_EOL,
    rootCauseId: 11, // battery is too old
    actionId: 2,     // replace node
    statusId: TICKET_STATUS.WAITING_FOR_ACTION,
    color: '#FF8200',
    shortcutKey: '3',
  },
  {
    key: 'tampering',
    label: 'Tampering / Damage',
    shortLabel: 'Tampering',
    ticketClassId: TICKET_CLASS_IDS.TAMPERING,
    rootCauseId: 21, // broken/damaged sensor
    actionId: 14,    // on-site investigation
    statusId: TICKET_STATUS.WAITING_FOR_ACTION,
    color: '#FF8200',
    shortcutKey: '4',
  },
  {
    key: 'firmware',
    label: 'Firmware Issue',
    shortLabel: 'Firmware',
    ticketClassId: TICKET_CLASS_IDS.FIRMWARE_ISSUE,
    rootCauseId: 7,  // outdated firmware
    actionId: 13,    // update firmware
    statusId: TICKET_STATUS.IN_PROGRESS,
    color: '#006EB7',
    shortcutKey: '5',
  },
  {
    key: 'out_of_range',
    label: 'Out of Range',
    shortLabel: 'Out of Range',
    ticketClassId: TICKET_CLASS_IDS.OUT_OF_RANGE,
    rootCauseId: 4,  // out of range
    actionId: 7,     // relocate node
    statusId: TICKET_STATUS.WAITING_FOR_ACTION,
    color: '#faad14',
    shortcutKey: '6',
  },
  {
    key: 'on_site',
    label: 'On-site Investigation',
    shortLabel: 'On-site',
    ticketClassId: TICKET_CLASS_IDS.ON_SITE_INVESTIGATION,
    rootCauseId: 13, // unknown
    actionId: 14,    // on-site investigation
    statusId: TICKET_STATUS.WAITING_FOR_ACTION,
    color: '#8c8c8c',
    shortcutKey: '7',
  },
  {
    key: 'false_alert',
    label: 'False Alert',
    shortLabel: 'False Alert',
    ticketClassId: TICKET_CLASS_IDS.FALSE_ALERT,
    rootCauseId: 34, // false alarm
    actionId: null,
    statusId: TICKET_STATUS.FALSE_ALERT,
    color: '#d9d9d9',
    shortcutKey: '8',
  },
  {
    key: 'close',
    label: 'Close Ticket',
    shortLabel: 'Close',
    ticketClassId: null, // no reclassification, just close
    rootCauseId: null,
    actionId: null,
    statusId: TICKET_STATUS.DONE,
    color: '#00A651',
    shortcutKey: '9',
  },
];

// --- Domain types ---
export interface Ticket {
  ticket_id: number;
  ticket_created: string;
  last_detected: string;
  priority: number;
  observable_id: string;
  observable_type: string;
  location_id: string;
  ticket_class_id: number;
  ticket_status_id: number;
  action_id: number | null;
  root_cause_id: number | null;
  comment: string | null;
  // Flat joined fields from REST API
  ticket_class: string;
  status_name: string;
  action_name: string | null;
  root_cause_name: string | null;
  location_name: string;
  observable_name: string | null;
  autoset_name: string | null;
  key_name: string | null;
  observable_info: string | null;
  position_type: string | null;
  key_id: string | null;
  is_online: boolean;
  past_ticket_count: number;
  cohort_size: number;
}

export interface LocationWithCounts {
  location_id: string;
  location_name: string;
  ticketCounts: {
    total: number;
    byClass: Record<number, number>;
  };
  issueCounts?: {
    total: number;
    suggested: number;
    confirmed: number;
    // All-stages roll-up — kept for callers that don't care about stage.
    byClass: Record<number, number>;
    // Stage-scoped roll-ups so the LocationPane chip counts can match the
    // total it sits next to. Mixing all-stages chips with a suggested-only
    // total was the source of "BAT 40 / UNK 302 / … but 0 to triage".
    byClassSuggested: Record<number, number>;
    byClassConfirmed: Record<number, number>;
  };
}

export interface Issue {
  issue_id: number;
  observable_id: string;
  observable_type: string;
  location_id: string;
  ticket_class_id: number | null;
  status_id: number;
  action_id: number | null;
  root_cause_id: number | null;
  confidence: number | null;
  classifier_version: string | null;
  classifier_notes: string | null;
  classifier_source: string | null;
  comment: string | null;
  priority: number | null;
  created_at: string;
  updated_at: string;
  classified_at: string | null;
  closed_at: string | null;
  // Flat joined fields from REST API
  ticket_class: string | null;
  status_name: string | null;
  action_name: string | null;
  root_cause_name: string | null;
  location_name: string;
  observable_name: string | null;
  autoset_name: string | null;
  key_name: string | null;
  observable_info: string | null;
  alert_count: number;
  // Freshest re-detection across this issue's still-open alerts
  // (MAX(tickets.last_detected)). This is the "is it still happening?" clock —
  // updated_at is NOT, it only moves when the sweeper reclassifies. NULL when
  // no open alert remains, which is itself the signal that the issue is stale.
  last_alert_detected: string | null;
  // Last heartbeat from sensorflow.nodes.last_seen — distinct from
  // created_at, which is when the issue row was opened. A node may have
  // been silent for months before the alert sweep noticed.
  node_last_seen: string | null;
  // Sub-cause tag from the latest classifier prediction. Only populated
  // when the surfaced class is OFFLINE_UNKNOWN (10); NULL on other classes
  // and on Unknown rows the rule chain couldn't narrow further (residual).
  // Values: tamper_suspect | fw_suspect | hw_drain | signal_drift
  unknown_subclass: UnknownSubclass | null;
  // --- Multi-operator coordination (2026-07-10) ---
  // Advisory claim ("I'm on it"). Cleared automatically on manual triage.
  // A claim older than the server TTL (30 min) is treated as stale by the UI.
  claimed_by?: string | null;
  claimed_at?: string | null;
  // Assets this node meters, one per CT channel. Populated for energy nodes
  // (energy / energy_switchless), whose key_name resolves to "unset" because
  // they map to a metering point, not a room key. Derived from
  // node_to_slot_mapping → stream_mappings → assets. Empty for non-energy
  // nodes. Only present on the single-issue detail fetch, not list rows.
  metered_assets?: MeteredAsset[];
}

export interface MeteredAsset {
  channel_index: number;
  asset_id: string | null;
  // Asset name, or the zone/position name when the metered subject is a zone.
  name: string | null;
  asset_type: string | null;
  hvac_system_type: string | null;
}

export type UnknownSubclass = 'tamper_suspect' | 'fw_suspect' | 'hw_drain' | 'signal_drift';

export interface IssueAlert {
  ticket_id: number;
  ticket_created: string;
  ticket_status_id: number;
  ticket_class_id: number;
  priority: number | null;
  observable_id: string;
  observable_type: string;
  last_detected: string | null;
  ticket_class: string | null;
  status_name: string | null;
  added_at: string;
  removed_at: string | null;
}

export interface BatteryStatus {
  day: string;
  avg_battery_lvl: number;
  estimated_battery_left_perc: number;
  months_of_life_left: number | null;
  current_age: number;
  battery_type: string;
  suggested_action: string;
}

export interface NodeHistory {
  started: string;
  ended: string | null;
  comment: string | null;
  event_type: string;
  location_name: string | null;
}

export interface LogEntry {
  id: number;
  log_text: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
}

export interface Gateway {
  balena_id: string;
  balena_device_name: string | null;
  network_status: string | null;
  is_online: boolean;
  last_heartbeat_time: string | null;
}

export interface TimeSeriesPoint {
  time: string;
  measurement_value: number;
}

export type StatusFilter = 'open' | 'in_progress' | 'ready_to_close' | 'all';
export type SortOrder = 'oldest' | 'newest' | 'priority';

// --- Action lookup ---
export interface Action {
  id: number;
  action: string;
  instruction_link: string | null;
  time_allocated_internal_mins: number | null;
  time_allocated_external_mins: number | null;
  equipment: string[];
}

export interface CreateActionPayload {
  action: string;
  instruction_link?: string | null;
  time_allocated_internal_mins?: number | null;
  time_allocated_external_mins?: number | null;
  equipment?: string[];
}

export type UpdateActionPayload = Partial<CreateActionPayload>;

// --- Task management types (sensorflow.tasks) ---
export type TaskStatus = 'pending' | 'in-progress' | 'done' | 'canceled' | 'accepted' | 'auto-resolved';

export interface Task {
  id: string;
  type: string;
  title: string;
  description: string | null;
  clickup_task_id: string;
  parent_entity_type: string;
  parent_entity_id: string;
  claimer_email: string | null;
  executor: string | null;
  status: TaskStatus;
  created_at: string;
  completed_at: string | null;
  support_ticket_id: number;
  issue_id: number | null;
  action_id: number | null;
  action_name: string | null;
  action_instruction_link: string | null;
  action_time_allocated_internal_mins: number | null;
  action_time_allocated_external_mins: number | null;
  action_equipment: string[];
  due_date: string | null;
}

export interface CreateTaskPayload {
  support_ticket_id?: number;
  issue_id?: number;
  title: string;
  description?: string;
  executor?: string;
  claimer_email?: string;
  due_date?: string;
  action_id?: number;
  parent_entity_type?: string;
  parent_entity_id?: string;
}

export interface UpdateTaskPayload {
  status?: TaskStatus;
  title?: string;
  description?: string | null;
  claimer_email?: string | null;
  executor?: string | null;
  due_date?: string | null;
}

// --- Location task view (task enriched with ticket/node context) ---
export interface LocationTask extends Task {
  ticket_id: number;
  ticket_class_id: number;
  priority: number;
  observable_id: string;
  observable_type: string;
  ticket_class: string;
  location_name: string;
  observable_name: string | null;
  autoset_name: string | null;
  key_name: string | null;
  observable_info: string | null;
  position_type: string | null;
  key_id: string | null;
}
